import { Col } from '@/components';
import { GovernmentSearchField } from '@/features/government-search/_components/GovernmentSearchField';
import { fetchGovernmentNumber } from '@/features/government-search/_utils/search-government';
import type { SharedSearchFormType } from '@/features/search/_models/shared-search';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import { type FC, memo, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFundSheetAnalysisPageSearchQueryStates } from '../../_hooks/search-query-states';

type Props = {
  resetCount: number;
};

/**
 * KS 分析 (基金シート) 用の「支出先名」フィールド。
 * RS 用 `PaymentColField` の KS 版で、`useAnalysisPageSearchQueryStates` ではなく
 * `useFundSheetAnalysisPageSearchQueryStates` を参照する。
 *
 * KS 分析タブは基金シート (standard) 固定のため、fundSheetType による非表示分岐は不要。
 * また `analysisTarget === 'payment'` のような必須化条件も KS では存在しない。
 */
export const KsPaymentColField: FC<Props> = memo(({ resetCount }) => {
  const { t } = useTranslation();

  const {
    control: sharedSearchControl,
    formState: { errors },
    setValue: setSharedSearchValue,
  } = useFormContext<SharedSearchFormType>();
  const {
    searchQueryStates: { corporateNumber },
  } = useFundSheetAnalysisPageSearchQueryStates();
  const isFirstRender = useRef(true);
  const prevValue = useRef<string | undefined>(undefined);
  const [defaultSearchValue, setDefaultSearchValue] = useState<
    string | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const prevResetCount = useRef(0);
  const [isResetProcessing, setIsResetProcessing] = useState(false);

  useEffect(() => {
    // booleanだと一瞬の変化で検知できないため数値で比較
    if (prevResetCount.current < resetCount) {
      prevResetCount.current = resetCount;
      setIsResetProcessing(true);
      // 一瞬すぎると値の更新を検知できないため、100ms後にfalseにする
      setTimeout(() => {
        setIsResetProcessing(false);
      }, 100);
    }
  }, [resetCount]);

  useEffect(() => {
    if (corporateNumber && isFirstRender.current) {
      (async () => {
        const res = await fetchGovernmentNumber({ corporateNumber });
        setDefaultSearchValue(res.at(0)?.corporateName);
        setIsLoading(false);
      })();
    } else {
      setIsLoading(false);
    }
    isFirstRender.current = false;
  }, [corporateNumber]);

  return (
    <Col span={8} style={{ minWidth: COL_MIN_WIDTH.large }}>
      {!isLoading && (
        <GovernmentSearchField
          name="corporateNumber"
          label={t('payment-name')}
          control={sharedSearchControl}
          errors={errors}
          isResetProcessing={isResetProcessing}
          defaultSearchValue={defaultSearchValue}
          onSelect={({ corporateInfo }) => {
            setSharedSearchValue(
              'corporateNumber',
              corporateInfo?.corporateNumber ?? null,
            );
          }}
          companyNameHint={t('hint-payment-name-selection')}
          companyNumberHint={t('hint-payment-name-selection-by-company-number')}
          onChange={(value) => {
            if (value !== prevValue.current) {
              setSharedSearchValue('corporateNumber', null);
            }
            prevValue.current = value;
          }}
        />
      )}
    </Col>
  );
});
